import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Upload, FileText, ScanLine, Maximize2, Minimize2, ChevronDown, ChevronUp, X, Camera, FolderOpen, Filter, Plus, Trash2 } from 'lucide-react';
import {
  BrioAnalyticsSurface, BrioFrame, BrioByCode, BrioTable,
  BrioDatePicker, BrioSelect, BrioIconButton,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import { useSettings } from '../../hooks/useSettings';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../supabaseClient';
import { scanReceiptFile } from '../../utils/ocrReceiptScanner';
import { findBestMatch } from '../../utils/expenseReceiptMatcher';
import {
  REIMBURSEMENT_STATUSES, OFFICE_EXPENSE_ID,
  OFFICE_OVERHEAD_CATEGORIES, PROJECT_EXPENSE_CATEGORIES,
} from '../../utils/expenseShared';

const PRESENTATION_CODE = '1b';

// ── Inline currency toggle (compact, BRIO gold, currency symbols) ────────
function CurrencyToggle({ value = 'INR', onChange, style }) {
  const btn = (c) => ({
    border: 'none', cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
    fontWeight: 600, fontSize: 10, padding: '2px 7px', lineHeight: 1.4,
    letterSpacing: '0.02em', transition: 'background 0.15s, color 0.15s',
    background: value === c ? 'var(--brio-gold, #c5a572)' : '#fff',
    color: value === c ? '#fff' : '#888',
  });
  return (
    <div style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden', border: '1px solid var(--brio-border, #d4d0c8)', flexShrink: 0, ...style }}>
      <button type="button" style={btn('INR')} onClick={() => onChange('INR')} title="Indian Rupees">₹</button>
      <button type="button" style={btn('EUR')} onClick={() => onChange('EUR')} title="Euros">€</button>
    </div>
  );
}

const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyRow = (currentUser) => ({
  projectId: '',
  userId: currentUser?.id || '',
  expenseDate: todayStr(),
  vendor: '',
  description: '',
  category: '',
  amount: '',
  currency: 'INR',
  officeLocation: 'IN',
  clientBillable: false,
  file: null,
  uploading: false,
  ocrScanning: false,
});

const cellStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '10px',
};

const statusDot = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'approved') return { color: 'var(--brio-orange, #DB6B5D)', label: 'Approved' };
  if (s === 'reimbursed') return { color: 'var(--brio-green, #84A48C)', label: 'Reimbursed' };
  if (s === 'voucher') return { color: 'var(--brio-teal, #689581)', label: 'In Voucher' };
  if (s === 'invoiced') return { color: 'var(--brio-gold, #9F902A)', label: 'Invoiced' };
  if (s === 'rejected') return { color: 'var(--brio-red, #934948)', label: 'Rejected' };
  return { color: 'var(--brio-gold, #9F902A)', label: 'Pending' };
};

/* -------------------------------------------------------------------------- */
/*  ExpenseClaimsPage                                                         */
/* -------------------------------------------------------------------------- */

export default function ExpenseClaimsPage({
  currentUser,
  isAdmin,
  activeUsers,
  activeProjects,
  getSortedProjects,
  fmtProject,
  reimbursementsHook,
  supabaseExpenses,
}) {
  const settings = useSettings();
  const {
    reimbursements,
    loading,
    createReimbursement,
    updateReimbursement,
    deleteReimbursement,
  } = reimbursementsHook;

  /* ---- local state ---- */
  const [editingRows, setEditingRows] = useState({});
  const [newCounter, setNewCounter] = useState(0);

  /* filters */
  const [filterMonth, setFilterMonth] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  /* fullscreen */
  const [isFullScreen, setIsFullScreen] = useState(true);

  /* mobile state */
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [mobileFormRowId, setMobileFormRowId] = useState(null);  // which editing row is open as full-screen form
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [scanSheetOpen, setScanSheetOpen] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);

  const scanInputRef = useRef(null);
  const mobileScanInputRef = useRef(null);
  const mobileCameraInputRef = useRef(null);
  const attachInputRef = useRef(null);
  const attachTargetRow = useRef(null);

  /* ---- derived data ---- */
  const myClaims = useMemo(() => {
    if (!reimbursements || !currentUser) return [];
    return reimbursements.filter((r) => r.userId === currentUser.id);
  }, [reimbursements, currentUser]);

  const filteredClaims = useMemo(() => {
    let rows = [...myClaims];

    if (filterMonth) {
      rows = rows.filter((r) => r.expenseDate && r.expenseDate.startsWith(filterMonth));
    }
    if (filterDateFrom) {
      rows = rows.filter((r) => r.expenseDate >= filterDateFrom);
    }
    if (filterDateTo) {
      rows = rows.filter((r) => r.expenseDate <= filterDateTo);
    }
    if (filterProject) {
      rows = rows.filter((r) => String(r.projectId) === String(filterProject));
    }
    if (filterStatus) {
      rows = rows.filter((r) => r.status === filterStatus);
    }

    rows.sort((a, b) => (b.expenseDate || '').localeCompare(a.expenseDate || ''));
    return rows;
  }, [myClaims, filterMonth, filterDateFrom, filterDateTo, filterProject, filterStatus]);

  const totalAmount = useMemo(() => {
    return filteredClaims.reduce((sum, r) => sum + (Number(r.amountInInr) || Number(r.amount) || 0), 0);
  }, [filteredClaims]);

  /* ---- project helpers ---- */
  const projectOptions = useMemo(() => {
    const sorted = getSortedProjects ? getSortedProjects() : (activeProjects || []);
    const opts = sorted.map((p) => ({
      value: p.id,
      label: fmtProject ? fmtProject(p) : (p.projectCode || p.name || `#${p.id}`),
    }));
    opts.unshift({ value: OFFICE_EXPENSE_ID, label: 'Office Expense' });
    return opts;
  }, [activeProjects, getSortedProjects, fmtProject]);

  const projectLabel = useCallback(
    (pid) => {
      if (pid === OFFICE_EXPENSE_ID) return 'Office Expense';
      const p = (activeProjects || []).find((pr) => String(pr.id) === String(pid));
      return p ? (fmtProject ? fmtProject(p) : (p.projectCode || p.name || `#${p.id}`)) : pid || '';
    },
    [activeProjects, fmtProject],
  );

  const userLabel = useCallback(
    (uid) => {
      const u = (activeUsers || []).find((usr) => String(usr.id) === String(uid));
      return u ? (u.name || u.email || `#${u.id}`) : '';
    },
    [activeUsers],
  );

  /* ---- editing helpers ---- */
  const updateEditingField = useCallback((rowId, field, value) => {
    setEditingRows((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], [field]: value },
    }));
  }, []);

  const startEditing = useCallback(
    (claim) => {
      setEditingRows((prev) => ({
        ...prev,
        [claim.id]: {
          projectId: claim.projectId || '',
          userId: claim.userId || currentUser?.id || '',
          expenseDate: claim.expenseDate || todayStr(),
          vendor: claim.vendor || '',
          description: claim.description || '',
          category: claim.category || '',
          amount: claim.amount ?? '',
          currency: claim.currency || 'INR',
          officeLocation: claim.officeLocation || 'IN',
          clientBillable: !!claim.clientBillable,
          file: null,
          uploading: false,
          ocrScanning: false,
        },
      }));
    },
    [currentUser],
  );

  const cancelEditingRow = useCallback((rowId) => {
    setEditingRows((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  const addBlankRow = useCallback(() => {
    const id = `new-${newCounter}`;
    setNewCounter((c) => c + 1);
    setEditingRows((prev) => ({
      ...prev,
      [id]: emptyRow(currentUser),
    }));
  }, [newCounter, currentUser]);

  /* ---- OCR scan ---- */
  const scanSingleFile = useCallback(
    async (file, id) => {
      updateEditingField(id, 'ocrScanning', true);
      try {
        const res = await scanReceiptFile(file);
        if (res?.ok && res.result) {
          const ocr = res.result;
          setEditingRows((prev) => {
            const row = prev[id];
            if (!row) return prev;
            return {
              ...prev,
              [id]: {
                ...row,
                amount: ocr.amount != null && !row.amount ? String(ocr.amount) : row.amount,
                currency: ocr.currency || row.currency,
                expenseDate: ocr.date || row.expenseDate,
                vendor: ocr.vendor || row.vendor,
                description: ocr.description || row.description,
                category: ocr.category || row.category,
                officeLocation: ocr.currency === 'EUR' ? 'NL' : ocr.currency === 'INR' ? 'IN' : row.officeLocation,
                ocrScanning: false,
              },
            };
          });
          return;
        }
      } catch (err) {
        console.error('[OCR scan]', err);
      }
      updateEditingField(id, 'ocrScanning', false);
    },
    [updateEditingField],
  );

  const handleScanFiles = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      files.forEach((file) => {
        const id = `new-${newCounter + files.indexOf(file)}`;
        setEditingRows((prev) => ({
          ...prev,
          [id]: { ...emptyRow(currentUser), file, ocrScanning: true },
        }));
        scanSingleFile(file, id);
      });
      setNewCounter((c) => c + files.length);
      e.target.value = '';
    },
    [newCounter, currentUser, scanSingleFile],
  );

  const handleAttachFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      const rowId = attachTargetRow.current;
      if (!file || !rowId) return;
      updateEditingField(rowId, 'file', file);
      /* auto-fill empty fields via OCR */
      const row = editingRows[rowId];
      if (row && (!row.amount && !row.vendor)) {
        updateEditingField(rowId, 'ocrScanning', true);
        scanSingleFile(file, rowId);
      }
      attachTargetRow.current = null;
      e.target.value = '';
    },
    [editingRows, updateEditingField, scanSingleFile],
  );

  /* ---- Dropbox upload ---- */
  const uploadToDropbox = useCallback(
    async (file, projectCode, expenseDate, vendor, description, officeLocation, isOfficeExpense) => {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const base64 = btoa(binary);

      const existingCount = myClaims.filter(
        (r) => String(r.projectId) === String(projectCode) && r.expenseDate === expenseDate,
      ).length;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Use manual fetch to avoid Supabase FunctionsClient header construction
      // issues that can cause "not a valid ByteString" errors for some users.
      // This mirrors the pattern used by callEdgeFunction in useSupabase.js.
      const safeFileName = file.name.replace(/[^\x20-\x7e]/g, '_');
      const uploadRes = await fetch(`${SUPABASE_URL}/functions/v1/upload-reimbursement-dropbox`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          fileBase64: base64,
          fileName: safeFileName,
          projectCode,
          expenseDate,
          vendor: vendor || '',
          description: description || 'receipt',
          sequenceNumber: existingCount + 1,
          officeLocation: officeLocation || 'IN',
          isOfficeExpense: !!isOfficeExpense,
        }),
      });

      if (!uploadRes.ok) {
        let detail = '';
        try {
          const errData = await uploadRes.json();
          detail = errData?.error || errData?.message || '';
        } catch { /* ignore */ }
        throw new Error(detail || `Upload failed (${uploadRes.status})`);
      }
      const result = await uploadRes.json();
      if (!result?.success) throw new Error(result?.error || 'Upload failed');
      return {
        fileName: result.fileName,
        fileDropboxPath: result.dropboxPath,
        fileDropboxUrl: result.dropboxUrl,
      };
    },
    [myClaims],
  );

  /* ---- save row ---- */
  const saveRow = useCallback(
    async (rowId) => {
      const row = editingRows[rowId];
      if (!row) return;

      const amt = parseFloat(row.amount);
      if (!amt || amt <= 0) {
        alert('Amount must be greater than 0');
        return;
      }
      if (!row.projectId) {
        alert('Please select a project');
        return;
      }
      if (!row.userId) {
        alert('Please select a team member');
        return;
      }

      const isOfficeExpense = row.projectId === OFFICE_EXPENSE_ID;

      updateEditingField(rowId, 'uploading', true);

      try {
        let fileFields = {};
        if (row.file) {
          const proj = isOfficeExpense ? null : (activeProjects || []).find((p) => String(p.id) === String(row.projectId));
          const pCode = isOfficeExpense ? 'OFFICE' : (proj?.projectCode || proj?.name?.substring(0, 12) || String(row.projectId));
          fileFields = await uploadToDropbox(
            row.file,
            pCode,
            row.expenseDate,
            row.vendor,
            row.description,
            row.officeLocation,
            isOfficeExpense,
          );
        }

        /* EUR -> INR conversion */
        let amountInInr = amt;
        if (row.currency === 'EUR' && settings?.fxRates?.eurToInr) {
          amountInInr = amt * settings.fxRates.eurToInr;
        }

        const data = {
          projectId: isOfficeExpense ? null : row.projectId,
          userId: row.userId,
          expenseDate: row.expenseDate,
          vendor: row.vendor,
          description: row.description,
          category: row.category,
          amount: amt,
          currency: row.currency,
          amountInInr,
          officeLocation: row.officeLocation,
          clientBillable: row.clientBillable,
          isOfficeExpense,
          ...fileFields,
        };

        const isNew = String(rowId).startsWith('new-');
        let result;
        if (isNew) {
          result = await createReimbursement(data);
        } else {
          result = await updateReimbursement(rowId, data);
        }

        /* auto-link to unlinked statement expense if match found */
        if (result && supabaseExpenses?.expenses) {
          try {
            const { reimbursement: matchedExp } = findBestMatch(result, supabaseExpenses.expenses);
            if (matchedExp) {
              await updateReimbursement(result.id, { linkedExpenseId: matchedExp.id });
            }
          } catch (linkErr) {
            console.error('[auto-link]', linkErr);
          }
        }

        cancelEditingRow(rowId);
      } catch (err) {
        console.error('[saveRow]', err);
        alert(`Save failed: ${err.message}`);
        updateEditingField(rowId, 'uploading', false);
      }
    },
    [
      editingRows, updateEditingField, cancelEditingRow,
      createReimbursement, updateReimbursement,
      uploadToDropbox, activeProjects, settings, supabaseExpenses,
    ],
  );

  /* ---- delete ---- */
  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm('Delete this claim?')) return;
      try {
        await deleteReimbursement(id);
      } catch (err) {
        alert(`Delete failed: ${err.message}`);
      }
    },
    [deleteReimbursement],
  );

  /* ---- month options ---- */
  const monthOptions = useMemo(() => {
    const months = new Set();
    myClaims.forEach((r) => {
      if (r.expenseDate) months.add(r.expenseDate.slice(0, 7));
    });
    return Array.from(months)
      .sort()
      .reverse()
      .map((m) => ({ value: m, label: m }));
  }, [myClaims]);

  /* ---- project filter options ---- */
  const projectFilterOptions = useMemo(() => {
    const ids = new Set(myClaims.map((r) => r.projectId).filter(Boolean));
    return [{ value: '', label: 'All Projects' }].concat(
      Array.from(ids).map((pid) => ({ value: pid, label: projectLabel(pid) })),
    );
  }, [myClaims, projectLabel]);

  /* ---- status options ---- */
  const statusOptions = useMemo(() => {
    const keys = REIMBURSEMENT_STATUSES ? Object.keys(REIMBURSEMENT_STATUSES) : ['pending', 'approved', 'rejected', 'paid'];
    return [{ value: '', label: 'All Statuses' }].concat(
      keys.map((s) => ({
        value: s,
        label: REIMBURSEMENT_STATUSES?.[s]?.label || (s.charAt(0).toUpperCase() + s.slice(1)),
      })),
    );
  }, []);

  /* ---- user options for Name column ---- */
  const userOptions = useMemo(() => {
    return (activeUsers || []).map((u) => ({
      value: u.id,
      label: u.name || u.email || `#${u.id}`,
    }));
  }, [activeUsers]);

  /* ---- category options for a row ---- */
  const getCategoryOptions = useCallback((projectId) => {
    const cats = projectId === OFFICE_EXPENSE_ID ? OFFICE_OVERHEAD_CATEGORIES : PROJECT_EXPENSE_CATEGORIES;
    return (cats || []).map((c) => ({ value: c, label: c }));
  }, []);

  /* ---- editing row IDs sorted newest first ---- */
  const editingRowIds = useMemo(() => {
    return Object.keys(editingRows).sort((a, b) => {
      const aNew = String(a).startsWith('new-');
      const bNew = String(b).startsWith('new-');
      if (aNew && !bNew) return -1;
      if (!aNew && bNew) return 1;
      return 0;
    });
  }, [editingRows]);

  /* ---- table props ---- */
  const tableProps = getTablePropsFromCode ? getTablePropsFromCode(PRESENTATION_CODE) : {};

  /* ---- mobile breakpoint detection ---- */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* ---- mobile scan handlers ---- */
  const handleMobileScanFiles = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      setScanSheetOpen(false);
      // Create editing rows and open the first one in the mobile form
      const firstId = `new-${newCounter}`;
      files.forEach((file, idx) => {
        const id = `new-${newCounter + idx}`;
        setEditingRows((prev) => ({
          ...prev,
          [id]: { ...emptyRow(currentUser), file, ocrScanning: true },
        }));
        scanSingleFile(file, id);
      });
      setNewCounter((c) => c + files.length);
      setMobileFormRowId(firstId);
      e.target.value = '';
    },
    [newCounter, currentUser, scanSingleFile],
  );

  const handleMobileAddBlank = useCallback(() => {
    const id = `new-${newCounter}`;
    setNewCounter((c) => c + 1);
    setEditingRows((prev) => ({
      ...prev,
      [id]: emptyRow(currentUser),
    }));
    setMobileFormRowId(id);
  }, [newCounter, currentUser]);

  const handleMobileEdit = useCallback(
    (claim) => {
      startEditing(claim);
      setMobileFormRowId(claim.id);
    },
    [startEditing],
  );

  const handleMobileSave = useCallback(
    async (rowId) => {
      // Capture remaining drafts BEFORE save removes the current row
      const remaining = Object.keys(editingRows).filter((id) => id !== rowId);
      await saveRow(rowId);
      // Advance to next pending draft, or close form
      if (remaining.length > 0) {
        setMobileFormRowId(remaining[0]);
      } else {
        setMobileFormRowId(null);
      }
    },
    [saveRow, editingRows],
  );

  const handleMobileFormCancel = useCallback(
    (rowId) => {
      cancelEditingRow(rowId);
      // If there are more editing rows (from multi-scan), open the next
      const remaining = Object.keys(editingRows).filter((id) => id !== rowId);
      if (remaining.length > 0) {
        setMobileFormRowId(remaining[0]);
      } else {
        setMobileFormRowId(null);
      }
    },
    [cancelEditingRow, editingRows],
  );

  const handleMobileAttachFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      const rowId = mobileFormRowId;
      if (!file || !rowId) return;
      updateEditingField(rowId, 'file', file);
      const row = editingRows[rowId];
      if (row && (!row.amount && !row.vendor)) {
        updateEditingField(rowId, 'ocrScanning', true);
        scanSingleFile(file, rowId);
      }
      e.target.value = '';
    },
    [mobileFormRowId, editingRows, updateEditingField, scanSingleFile],
  );

  // Count active filters for badge
  const activeFilterCount = [filterMonth, filterDateFrom, filterDateTo, filterProject, filterStatus].filter(Boolean).length;

  const clearAllFilters = useCallback(() => {
    setFilterMonth('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterProject('');
    setFilterStatus('');
  }, []);

  /* ======================================================================== */
  /*  RENDER                                                                  */
  /* ======================================================================== */

  const renderEditingRow = (rowId) => {
    const row = editingRows[rowId];
    if (!row) return null;
    const isNew = String(rowId).startsWith('new-');
    const isOffice = row.projectId === OFFICE_EXPENSE_ID;

    return (
      <tr key={rowId} style={{ background: 'var(--brio-surface-accent, #f5f3e8)' }}>
        {/* Date */}
        <td style={cellStyle}>
          <input
            type="date"
            value={row.expenseDate}
            onChange={(e) => updateEditingField(rowId, 'expenseDate', e.target.value)}
            style={{ fontSize: '10px', width: '100%', border: '1px solid var(--brio-border)', borderRadius: 3, padding: '2px 4px' }}
          />
        </td>

        {/* Name */}
        <td style={cellStyle}>
          <select
            value={row.userId}
            onChange={(e) => updateEditingField(rowId, 'userId', e.target.value)}
            style={{ fontSize: '10px', width: '100%', border: '1px solid var(--brio-border)', borderRadius: 3, padding: '2px 4px' }}
          >
            <option value="">--</option>
            {userOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </td>

        {/* Project */}
        <td style={cellStyle}>
          <select
            value={row.projectId}
            onChange={(e) => {
              updateEditingField(rowId, 'projectId', e.target.value);
              updateEditingField(rowId, 'category', '');
            }}
            style={{ fontSize: '10px', width: '100%', border: '1px solid var(--brio-border)', borderRadius: 3, padding: '2px 4px' }}
          >
            <option value="">-- select --</option>
            {projectOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </td>

        {/* Vendor */}
        <td style={cellStyle}>
          <input
            type="text"
            value={row.vendor}
            onChange={(e) => updateEditingField(rowId, 'vendor', e.target.value)}
            placeholder="Vendor"
            style={{ fontSize: '10px', width: '100%', border: '1px solid var(--brio-border)', borderRadius: 3, padding: '2px 4px' }}
          />
        </td>

        {/* Description */}
        <td style={cellStyle}>
          <input
            type="text"
            value={row.description}
            onChange={(e) => updateEditingField(rowId, 'description', e.target.value)}
            placeholder="Description"
            style={{ fontSize: '10px', width: '100%', border: '1px solid var(--brio-border)', borderRadius: 3, padding: '2px 4px' }}
          />
        </td>

        {/* Category */}
        <td style={cellStyle}>
          <select
            value={row.category}
            onChange={(e) => updateEditingField(rowId, 'category', e.target.value)}
            style={{ fontSize: '10px', width: '100%', border: '1px solid var(--brio-border)', borderRadius: 3, padding: '2px 4px' }}
          >
            <option value="">-- category --</option>
            {getCategoryOptions(row.projectId).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </td>

        {/* Office */}
        <td style={cellStyle}>
          <select
            value={row.officeLocation}
            onChange={(e) => updateEditingField(rowId, 'officeLocation', e.target.value)}
            style={{ fontSize: '10px', width: '100%', border: '1px solid var(--brio-border)', borderRadius: 3, padding: '2px 4px' }}
          >
            <option value="IN">IN</option>
            <option value="NL">NL</option>
          </select>
        </td>

        {/* Billable */}
        <td style={{ ...cellStyle, textAlign: 'center' }}>
          <input
            type="checkbox"
            className="brio-checkbox"
            checked={row.clientBillable}
            onChange={(e) => updateEditingField(rowId, 'clientBillable', e.target.checked)}
          />
        </td>

        {/* Amount */}
        <td style={cellStyle}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <CurrencyToggle
              value={row.currency}
              onChange={(c) => {
                updateEditingField(rowId, 'currency', c);
                updateEditingField(rowId, 'officeLocation', c === 'EUR' ? 'NL' : 'IN');
              }}
            />
            <input
              type="text"
              inputMode="decimal"
              value={row.amount}
              onChange={(e) => updateEditingField(rowId, 'amount', e.target.value.replace(',', '.'))}
              placeholder="0"
              style={{ fontSize: '10px', width: '100%', border: '1px solid var(--brio-border)', borderRadius: 3, padding: '2px 4px' }}
            />
          </div>
        </td>

        {/* Status */}
        <td style={cellStyle}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '9px', whiteSpace: 'nowrap' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--brio-gold, #9F902A)', flexShrink: 0 }} />
            {isNew ? 'New' : 'Pending'}
          </span>
        </td>

        {/* File */}
        <td style={cellStyle}>
          {row.ocrScanning ? (
            <span style={{ fontSize: '9px', color: 'var(--brio-gold)' }}>Scanning...</span>
          ) : row.file ? (
            <span
              style={{ fontSize: '9px', color: 'var(--brio-gold)', cursor: 'pointer' }}
              title={row.file.name}
            >
              <FileText size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />{' '}
              {row.file.name.length > 10 ? row.file.name.slice(0, 10) + '...' : row.file.name}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                attachTargetRow.current = rowId;
                attachInputRef.current?.click();
              }}
              style={{ fontSize: '9px', color: 'var(--brio-grey)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >
              <Upload size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /> Attach
            </button>
          )}
        </td>

        {/* Actions */}
        <td style={{ ...cellStyle, textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
            <button
              className="brio-btn brio-btn-primary"
              onClick={() => saveRow(rowId)}
              disabled={row.uploading || row.ocrScanning}
              style={{ fontSize: '9px', padding: '2px 8px', lineHeight: 1.4 }}
            >
              {row.uploading ? 'Saving...' : 'Save'}
            </button>
            <button
              className="brio-btn brio-btn-secondary"
              onClick={() => cancelEditingRow(rowId)}
              disabled={row.uploading}
              style={{ fontSize: '9px', padding: '2px 8px', lineHeight: 1.4 }}
            >
              Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderClaimRow = (claim) => {
    const isPending = (claim.status || 'pending').toLowerCase() === 'pending';

    return (
      <tr key={claim.id}>
        {/* Date */}
        <td style={cellStyle}>{claim.expenseDate || ''}</td>

        {/* Name */}
        <td style={cellStyle}>{userLabel(claim.userId)}</td>

        {/* Project */}
        <td style={cellStyle} title={projectLabel(claim.projectId)}>
          {(!claim.projectId) ? 'Office Expense' : projectLabel(claim.projectId)}
        </td>

        {/* Vendor */}
        <td style={cellStyle} title={claim.vendor || ''}>{claim.vendor || ''}</td>

        {/* Description */}
        <td style={cellStyle} title={claim.description || ''}>{claim.description || ''}</td>

        {/* Category */}
        <td style={cellStyle}>{claim.category || ''}</td>

        {/* Office */}
        <td style={cellStyle}>{claim.officeLocation || ''}</td>

        {/* Billable */}
        <td style={{ ...cellStyle, textAlign: 'center' }}>
          {claim.clientBillable ? 'Y' : ''}
        </td>

        {/* Amount */}
        <td style={{ ...cellStyle, textAlign: 'right' }}>
          <span style={{ fontSize: '10px' }}>
            {(claim.currency || 'INR') === 'EUR' ? '€' : '₹'}{' '}
            {Number(claim.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </td>

        {/* Status */}
        <td style={cellStyle}>
          {(() => { const d = statusDot(claim.status); return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '9px', whiteSpace: 'nowrap' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: d.color, flexShrink: 0 }} />
              {d.label}
            </span>
          ); })()}
        </td>

        {/* File */}
        <td style={cellStyle}>
          {claim.fileDropboxUrl ? (
            <a
              href={claim.fileDropboxUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '9px', color: 'var(--brio-gold)' }}
              title={claim.fileName || 'Receipt'}
            >
              <FileText size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />{' '}
              View
            </a>
          ) : (
            <span style={{ fontSize: '9px', color: 'var(--brio-grey)' }}>--</span>
          )}
        </td>

        {/* Actions */}
        <td style={{ ...cellStyle, textAlign: 'center' }}>
          {isPending && (
            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
              <BrioIconButton icon="edit" variant="edit" size="small"
                title="Edit claim" onClick={() => startEditing(claim)} />
              <BrioIconButton icon="delete" variant="delete" size="small"
                title="Delete claim" onClick={() => handleDelete(claim.id)} />
            </div>
          )}
        </td>
      </tr>
    );
  };

  /* ======================================================================== */
  /*  MOBILE RENDER HELPERS                                                   */
  /* ======================================================================== */

  const renderMobileClaimCard = (claim) => {
    const isPending = (claim.status || 'pending').toLowerCase() === 'pending';
    const isExpanded = expandedCardId === claim.id;
    const dot = statusDot(claim.status);
    const isBeingEdited = !!editingRows[claim.id];

    return (
      <div key={claim.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--brio-border, #e0e0e0)', marginBottom: 10, overflow: 'hidden' }}>
        {/* Collapsed card header */}
        <button type="button" onClick={() => setExpandedCardId(isExpanded ? null : claim.id)}
          style={{ width: '100%', background: 'none', border: 'none', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer' }}>
          <div style={{ minWidth: 70, textAlign: 'right' }}>
            <span style={{ fontSize: 18, fontWeight: 600, color: '#000' }}>
              {(claim.currency || 'INR') === 'EUR' ? '\u20AC' : '\u20B9'}
              {Number(claim.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {claim.vendor || claim.description || 'No description'}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
              {(!claim.projectId) ? 'Office Expense' : projectLabel(claim.projectId)}
              {' \u00B7 '}{claim.expenseDate || ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {claim.fileDropboxUrl && <FileText size={14} style={{ color: 'var(--brio-gold, #9F902A)' }} />}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 10, background: dot.color + '18', color: dot.color }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot.color }} />
              {dot.label}
            </span>
            {isExpanded ? <ChevronUp size={16} style={{ color: '#999' }} /> : <ChevronDown size={16} style={{ color: '#999' }} />}
          </div>
        </button>
        {/* Expanded details */}
        {isExpanded && (
          <div style={{ padding: '0 16px 14px', borderTop: '1px solid #f0f0f0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', paddingTop: 12, fontSize: 12 }}>
              <div><span style={{ color: '#999' }}>Category</span><div>{claim.category || '--'}</div></div>
              <div><span style={{ color: '#999' }}>Office</span><div>{claim.officeLocation || '--'}</div></div>
              <div><span style={{ color: '#999' }}>Vendor</span><div>{claim.vendor || '--'}</div></div>
              <div><span style={{ color: '#999' }}>Billable</span><div>{claim.clientBillable ? 'Yes' : 'No'}</div></div>
              {claim.description && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#999' }}>Description</span><div>{claim.description}</div></div>}
              {claim.fileDropboxUrl && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <a href={claim.fileDropboxUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--brio-gold)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <FileText size={14} /> View Receipt
                  </a>
                </div>
              )}
            </div>
            {isPending && !isBeingEdited && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                <button className="brio-btn brio-btn-primary" onClick={() => handleMobileEdit(claim)} style={{ flex: 1, fontSize: 13, padding: '10px 0' }}>Edit</button>
                <button className="brio-btn brio-btn-secondary" onClick={() => handleDelete(claim.id)} style={{ fontSize: 13, padding: '10px 16px', color: '#c0392b' }}><Trash2 size={14} /></button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  /* ---- Mobile full-screen form ---- */
  const renderMobileForm = () => {
    const rowId = mobileFormRowId;
    const row = editingRows[rowId];
    if (!row) return null;
    const isNew = String(rowId).startsWith('new-');
    const isOffice = row.projectId === OFFICE_EXPENSE_ID;
    const catOpts = getCategoryOptions(row.projectId);

    const fieldLabel = { fontSize: 11, fontWeight: 500, color: '#888', marginBottom: 4, display: 'block', letterSpacing: '0.03em' };
    const fieldInput = { width: '100%', fontSize: 15, padding: '10px 12px', border: '1px solid var(--brio-border, #ddd)', borderRadius: 8, background: '#fff', fontFamily: "'Raleway', sans-serif" };

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#fff', display: 'flex', flexDirection: 'column' }}>
        {/* Form header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #eee' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{isNew ? 'New Claim' : 'Edit Claim'}</h2>
          <button onClick={() => handleMobileFormCancel(rowId)} style={{ background: 'none', border: 'none', padding: 8, cursor: 'pointer' }}><X size={20} /></button>
        </div>

        {/* OCR status banner */}
        {row.ocrScanning && (
          <div style={{ background: '#FFF8E1', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <ScanLine size={16} style={{ color: 'var(--brio-gold)', animation: 'spin 1s linear infinite' }} />
            <span>Scanning receipt...</span>
          </div>
        )}
        {row.file && !row.ocrScanning && isNew && (
          <div style={{ background: '#E8F5E9', padding: '10px 16px', fontSize: 12, color: '#2E7D32' }}>
            Receipt scanned. Please review and correct the fields below.
          </div>
        )}

        {/* Scrollable form body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', paddingBottom: 100 }}>
          {/* Section 1: Core */}
          <div style={{ marginBottom: 20 }}>
            <label style={fieldLabel}>Project</label>
            <select value={row.projectId} onChange={(e) => { updateEditingField(rowId, 'projectId', e.target.value); updateEditingField(rowId, 'category', ''); }} style={fieldInput}>
              <option value="">-- Select project --</option>
              {projectOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={fieldLabel}>Date</label>
              <input type="date" value={row.expenseDate} onChange={(e) => updateEditingField(rowId, 'expenseDate', e.target.value)} style={fieldInput} />
            </div>
            <div>
              <label style={fieldLabel}>Office</label>
              <select value={row.officeLocation} onChange={(e) => updateEditingField(rowId, 'officeLocation', e.target.value)} style={fieldInput}>
                <option value="IN">India</option>
                <option value="NL">Netherlands</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={fieldLabel}>Currency</label>
              <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--brio-border, #ddd)', height: 44 }}>
                <button type="button" onClick={() => { updateEditingField(rowId, 'currency', 'INR'); updateEditingField(rowId, 'officeLocation', 'IN'); }}
                  style={{ flex: 1, border: 'none', fontSize: 16, fontWeight: 600, cursor: 'pointer', background: row.currency === 'INR' ? 'var(--brio-gold, #9F902A)' : '#fff', color: row.currency === 'INR' ? '#fff' : '#888' }}>{'\u20B9'}</button>
                <button type="button" onClick={() => { updateEditingField(rowId, 'currency', 'EUR'); updateEditingField(rowId, 'officeLocation', 'NL'); }}
                  style={{ flex: 1, border: 'none', fontSize: 16, fontWeight: 600, cursor: 'pointer', background: row.currency === 'EUR' ? 'var(--brio-gold, #9F902A)' : '#fff', color: row.currency === 'EUR' ? '#fff' : '#888' }}>{'\u20AC'}</button>
              </div>
            </div>
            <div>
              <label style={fieldLabel}>Amount</label>
              <input type="text" inputMode="decimal" value={row.amount} onChange={(e) => updateEditingField(rowId, 'amount', e.target.value.replace(',', '.'))}
                placeholder="0.00" style={{ ...fieldInput, fontSize: 20, fontWeight: 600 }} />
            </div>
          </div>

          {/* Section 2: Receipt details */}
          <div style={{ marginBottom: 20 }}>
            <label style={fieldLabel}>Vendor</label>
            <input type="text" value={row.vendor} onChange={(e) => updateEditingField(rowId, 'vendor', e.target.value)} placeholder="e.g. Uber, Amazon" style={fieldInput} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={fieldLabel}>Description</label>
            <input type="text" value={row.description} onChange={(e) => updateEditingField(rowId, 'description', e.target.value)} placeholder="What was this expense for?" style={fieldInput} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={fieldLabel}>Category</label>
            <select value={row.category} onChange={(e) => updateEditingField(rowId, 'category', e.target.value)} style={fieldInput}>
              <option value="">-- Select category --</option>
              {catOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Section 3: Billing */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={row.clientBillable} onChange={(e) => updateEditingField(rowId, 'clientBillable', e.target.checked)}
                style={{ width: 20, height: 20, accentColor: 'var(--brio-gold, #9F902A)' }} />
              Client billable
            </label>
          </div>

          {/* Section 4: Attachment */}
          <div style={{ padding: 16, background: '#f9f9f7', borderRadius: 10, border: '1px dashed var(--brio-border, #ddd)' }}>
            <label style={fieldLabel}>Receipt</label>
            {row.ocrScanning ? (
              <div style={{ textAlign: 'center', padding: 16, color: 'var(--brio-gold)' }}>
                <ScanLine size={24} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: 8, fontSize: 13 }}>Scanning receipt...</div>
              </div>
            ) : row.file ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FileText size={20} style={{ color: 'var(--brio-gold)', flexShrink: 0 }} />
                <span style={{ fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.file.name}</span>
                <button type="button" onClick={() => updateEditingField(rowId, 'file', null)}
                  style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', padding: 4 }}><X size={16} /></button>
              </div>
            ) : (
              <button type="button" onClick={() => attachInputRef.current?.click()}
                style={{ width: '100%', padding: '14px', background: '#fff', border: '1px solid var(--brio-border)', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, color: '#555' }}>
                <Upload size={18} /> Attach Receipt
              </button>
            )}
            <input ref={attachInputRef} type="file" accept="image/*,.pdf" onChange={handleMobileAttachFile} style={{ display: 'none' }} />
          </div>
        </div>

        {/* Sticky bottom actions */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #eee', background: '#fff', display: 'flex', gap: 10, paddingBottom: 'env(safe-area-inset-bottom, 12px)' }}>
          <button className="brio-btn brio-btn-secondary" onClick={() => handleMobileFormCancel(rowId)} disabled={row.uploading}
            style={{ flex: 1, fontSize: 15, padding: '12px 0' }}>Cancel</button>
          <button className="brio-btn brio-btn-primary" onClick={() => handleMobileSave(rowId)} disabled={row.uploading || row.ocrScanning}
            style={{ flex: 2, fontSize: 15, padding: '12px 0', fontWeight: 600 }}>
            {row.uploading ? 'Saving...' : 'Save Claim'}
          </button>
        </div>
      </div>
    );
  };

  /* ---- Mobile filter bottom sheet ---- */
  const renderFilterSheet = () => {
    if (!filterSheetOpen) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setFilterSheetOpen(false)}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#fff', borderRadius: '16px 16px 0 0', maxHeight: '70vh', overflowY: 'auto', paddingBottom: 'env(safe-area-inset-bottom, 16px)', animation: 'mobileSheetSlideUp 0.2s ease-out' }}
          onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 8 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#ddd' }} />
          </div>
          <div style={{ padding: '0 20px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Filters</h3>
              {activeFilterCount > 0 && (
                <button onClick={clearAllFilters} style={{ background: 'none', border: 'none', color: 'var(--brio-gold)', fontSize: 13, cursor: 'pointer' }}>Clear all</button>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#888', marginBottom: 4, display: 'block' }}>Month</label>
                <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #ddd', borderRadius: 8 }}>
                  <option value="">All months</option>
                  {monthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#888', marginBottom: 4, display: 'block' }}>From</label>
                  <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #ddd', borderRadius: 8 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', marginBottom: 4, display: 'block' }}>To</label>
                  <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #ddd', borderRadius: 8 }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', marginBottom: 4, display: 'block' }}>Project</label>
                <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #ddd', borderRadius: 8 }}>
                  <option value="">All Projects</option>
                  {projectFilterOptions.filter(o => o.value !== '').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', marginBottom: 4, display: 'block' }}>Status</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #ddd', borderRadius: 8 }}>
                  {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <button onClick={() => setFilterSheetOpen(false)} className="brio-btn brio-btn-primary"
              style={{ width: '100%', marginTop: 20, padding: '12px 0', fontSize: 15 }}>Done</button>
          </div>
        </div>
      </div>
    );
  };

  /* ---- Mobile scan action sheet ---- */
  const renderScanSheet = () => {
    if (!scanSheetOpen) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setScanSheetOpen(false)}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#fff', borderRadius: '16px 16px 0 0', paddingBottom: 'env(safe-area-inset-bottom, 16px)', animation: 'mobileSheetSlideUp 0.2s ease-out' }}
          onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 8 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#ddd' }} />
          </div>
          <div style={{ padding: '0 20px 20px' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>Scan Receipt</h3>
            <button onClick={() => mobileCameraInputRef.current?.click()}
              style={{ width: '100%', padding: '14px 16px', marginBottom: 10, background: '#fff', border: '1px solid var(--brio-border)', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, fontSize: 15 }}>
              <Camera size={20} style={{ color: 'var(--brio-gold)' }} /> Take Photo
            </button>
            <button onClick={() => mobileScanInputRef.current?.click()}
              style={{ width: '100%', padding: '14px 16px', marginBottom: 10, background: '#fff', border: '1px solid var(--brio-border)', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, fontSize: 15 }}>
              <FolderOpen size={20} style={{ color: 'var(--brio-gold)' }} /> Upload from Files
            </button>
            <button onClick={() => setScanSheetOpen(false)}
              style={{ width: '100%', padding: '14px 16px', background: '#f5f5f5', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, color: '#888' }}>
              Cancel
            </button>
            <input ref={mobileCameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleMobileScanFiles} style={{ display: 'none' }} />
            <input ref={mobileScanInputRef} type="file" accept="image/*,.pdf" multiple onChange={handleMobileScanFiles} style={{ display: 'none' }} />
          </div>
        </div>
      </div>
    );
  };

  /* ---- Pending scan drafts indicator (for multi-scan) ---- */
  const pendingDraftCount = editingRowIds.filter((id) => id !== mobileFormRowId).length;

  /* ======================================================================== */
  /*  RETURN                                                                  */
  /* ======================================================================== */
  // Inset by the nav bars App.js publishes, and stacked below them, so full
  // screen fills the content area without hiding the navigation. Same contract
  // as useFullScreenPage in BrioComponents, so keep the two in step.
  const fullScreenStyle = isFullScreen
    ? { position: 'fixed', top: 'var(--brio-nav-height, 0px)', left: 0, right: 0, bottom: 'var(--brio-nav-bottom-height, 0px)', zIndex: 40, backgroundColor: '#fff', display: 'flex', flexDirection: 'column', overflow: 'auto' }
    : {};

  /* ---- MOBILE VIEW ---- */
  if (isMobile) {
    return (
      <div style={{ background: '#f5f5f3', minHeight: '100vh' }}>
        {/* Mobile form overlay */}
        {mobileFormRowId && renderMobileForm()}
        {/* Filter sheet */}
        {renderFilterSheet()}
        {/* Scan sheet */}
        {renderScanSheet()}

        {/* Mobile header */}
        <div style={{ background: '#fff', padding: '14px 16px', borderBottom: '1px solid #eee', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Expense Claims</h1>
            <button onClick={() => setFilterSheetOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, position: 'relative' }}>
              <Filter size={20} style={{ color: activeFilterCount > 0 ? 'var(--brio-gold)' : '#888' }} />
              {activeFilterCount > 0 && (
                <span style={{ position: 'absolute', top: 0, right: 0, width: 16, height: 16, borderRadius: '50%', background: 'var(--brio-gold, #9F902A)', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setScanSheetOpen(true)}
              style={{ flex: 2, padding: '12px 0', borderRadius: 10, border: 'none', background: '#000', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <ScanLine size={18} /> Scan Bill
            </button>
            <button onClick={handleMobileAddBlank}
              style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: '1px solid var(--brio-border)', background: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Plus size={16} /> Manual
            </button>
          </div>
        </div>

        {/* Pending drafts banner */}
        {pendingDraftCount > 0 && !mobileFormRowId && (
          <div style={{ margin: '10px 16px 0', padding: '10px 14px', background: '#FFF8E1', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
            <span>{pendingDraftCount} unsaved draft{pendingDraftCount > 1 ? 's' : ''}</span>
            <button onClick={() => setMobileFormRowId(editingRowIds[0])}
              style={{ background: 'none', border: 'none', color: 'var(--brio-gold)', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Review</button>
          </div>
        )}

        {/* Claims list */}
        <div style={{ padding: '10px 16px 80px' }}>
          {/* Summary */}
          {filteredClaims.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, fontSize: 12, color: '#888' }}>
              <span>{filteredClaims.length} claim{filteredClaims.length !== 1 ? 's' : ''}</span>
              <span style={{ fontWeight: 600, color: '#333' }}>
                Total: {'\u20B9'}{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999', fontSize: 14 }}>Loading...</div>
          ) : filteredClaims.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              <ScanLine size={32} style={{ color: '#ccc', marginBottom: 12 }} />
              <div style={{ fontSize: 14 }}>No expense claims yet</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Scan a receipt or add a manual claim</div>
            </div>
          ) : (
            filteredClaims.map((claim) => renderMobileClaimCard(claim))
          )}
        </div>
      </div>
    );
  }

  /* ---- DESKTOP VIEW ---- */
  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff', ...fullScreenStyle }}>
      {/* HEADER BAND */}
      <div className="brio-pageband brio-pageband--header">
        {React.createElement(isFullScreen ? 'div' : BrioFrame, isFullScreen ? { style: { width: '100%', padding: '0 24px' } } : null,
          <div className="brio-page-header__content">
            <div className="brio-flex brio-items-center brio-gap-xs" style={{ flexWrap: 'nowrap' }}>
              <BrioSelect value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} style={{ padding: '4px 8px', fontSize: '11px', minWidth: 70, background: '#fff' }}>
                <option value="">All</option>
                {monthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </BrioSelect>
              <BrioDatePicker value={filterDateFrom} onChange={setFilterDateFrom} className="brio-datepicker-trigger--compact" placeholder="From" style={{ width: 110, fontSize: '10px', background: '#fff' }} clearable />
              <BrioDatePicker value={filterDateTo} onChange={setFilterDateTo} className="brio-datepicker-trigger--compact" placeholder="To" style={{ width: 110, fontSize: '10px', background: '#fff' }} clearable />
              <BrioSelect value={filterProject} onChange={(e) => setFilterProject(e.target.value)} style={{ padding: '4px 8px', fontSize: '11px', background: '#fff' }}>
                <option value="">All Projects</option>
                {projectFilterOptions.filter(o => o.value !== '').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </BrioSelect>
              <BrioSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: '4px 8px', fontSize: '11px', minWidth: 100, background: '#fff' }}>
                {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </BrioSelect>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="brio-btn brio-btn-secondary" onClick={() => scanInputRef.current?.click()} style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <ScanLine size={13} /> Scan Bill
              </button>
              <button className="brio-btn brio-btn-primary" onClick={addBlankRow} style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                + Manual Claim
              </button>
              <button onClick={() => setIsFullScreen(!isFullScreen)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--brio-grey, #888)' }} title={isFullScreen ? 'Exit full screen' : 'Full screen'}>
                {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <input ref={scanInputRef} type="file" accept="image/*,.pdf" multiple onChange={handleScanFiles} style={{ display: 'none' }} />
              <input ref={attachInputRef} type="file" accept="image/*,.pdf" onChange={handleAttachFile} style={{ display: 'none' }} />
            </div>
          </div>
        )}
      </div>

      {React.createElement(isFullScreen ? 'div' : BrioFrame, isFullScreen ? { className: 'brio-fullscreen-content', style: { width: '100%', padding: '0 24px' } } : null,
        <BrioAnalyticsSurface padding="m">
          <BrioByCode code={PRESENTATION_CODE} className="!overflow-visible">
            <BrioTable {...tableProps} style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '8%' }} /><col style={{ width: '9%' }} /><col style={{ width: '12%' }} />
                <col style={{ width: '8%' }} /><col style={{ width: '14%' }} /><col style={{ width: '10%' }} />
                <col style={{ width: '4%' }} /><col style={{ width: '3%' }} /><col style={{ width: '12%' }} />
                <col style={{ width: '6%' }} /><col style={{ width: '4%' }} /><col style={{ width: '10%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ fontSize: '10px' }}>Date</th><th style={{ fontSize: '10px' }}>Name</th>
                  <th style={{ fontSize: '10px' }}>Project</th><th style={{ fontSize: '10px' }}>Vendor</th>
                  <th style={{ fontSize: '10px' }}>Description</th><th style={{ fontSize: '10px' }}>Category</th>
                  <th style={{ fontSize: '10px' }}>Office</th><th style={{ fontSize: '10px', textAlign: 'center' }}>Bill</th>
                  <th style={{ fontSize: '10px', textAlign: 'right' }}>Amount</th><th style={{ fontSize: '10px' }}>Status</th>
                  <th style={{ fontSize: '10px' }}>File</th><th style={{ fontSize: '10px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {editingRowIds.map((rowId) => renderEditingRow(rowId))}
                {loading ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: 24, fontSize: '11px', color: 'var(--brio-grey)' }}>Loading...</td></tr>
                ) : filteredClaims.length === 0 && editingRowIds.length === 0 ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: 24, fontSize: '11px', color: 'var(--brio-grey)' }}>No expense claims found. Click "+ Add Row" or "Scan Bill" to create one.</td></tr>
                ) : (
                  filteredClaims.filter((c) => !editingRows[c.id]).map((claim) => renderClaimRow(claim))
                )}
              </tbody>
            </BrioTable>
          </BrioByCode>
          {filteredClaims.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px', borderTop: '1px solid var(--brio-border)', fontSize: '11px', fontWeight: 600 }}>
              <span style={{ color: 'var(--brio-grey)', marginRight: 8 }}>{filteredClaims.length} claim{filteredClaims.length !== 1 ? 's' : ''}</span>
              <span>Total: INR {totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          )}
        </BrioAnalyticsSurface>
      )}
    </div>
  );
}
